import moment from "moment";

import type { UserInteractionsContract } from "@btnet/services-btnet/handlers/models/userInteractionsContract";

import { type BTSelectItem, type IBaseEntity } from "types/apiResponse/apiResponse";
import { type BTLoginTypes, type DiscussionsLinkedTypes } from "types/enum";

import { type ApprovalStatus } from "commonComponents/approvals/ApprovalViewer/ApprovalViewer.types";
import {
    EditorContent,
    type IEditorApiType,
} from "commonComponents/btWrappers/editor/editor.types";

export type CommentFormActions = undefined | "send" | "sendAndClose";

export interface ICommentFormValues {
    commentJson?: EditorContent;
    linkedType: number;
    notificationUsers: number[];
    mentionableUsers: number[];
    showOwner: boolean;
    showSubs: boolean;
    channelId?: number | null;
}

export interface ICommentInfo {
    /** populated when legacy comment */
    discussionText?: string;
    /** populated when new JSON comment format */
    commentJson?: EditorContent;
    linkedType: DiscussionsLinkedTypes;
    notificationUsers: BTSelectItem[];
    mentionableUsers: IMentionableUser[];
    showOwner: boolean;
    showSubs: boolean;
    channelId?: number | null;
}

export interface IMentionableUser {
    id: number;
    name: string;
    type?: BTLoginTypes;
}

export interface ICommentPermissionsData {
    canDelete?: boolean;
    canShowOwner?: boolean;
    canShowSubs?: boolean;
    defaultShowSubs?: boolean;
    defaultShowOwner?: boolean;
}

export class CommentPermissions {
    constructor(data: ICommentPermissionsData) {
        this.canDelete = data.canDelete || false; // default to false

        this.canShowOwner = data.canShowOwner ?? false;
        this.canShowSubs = data.canShowSubs ?? false;

        this.defaultShowSubs = data.defaultShowSubs ?? false;
        this.defaultShowOwner = data.defaultShowOwner ?? false;
    }

    canDelete: boolean;

    canShowOwner: boolean;
    canShowSubs: boolean;

    defaultShowSubs: boolean;
    defaultShowOwner: boolean;
}

export interface IDiscussionCommentData extends ICommentPermissionsData {
    commentID?: number;
    jobsiteID?: number;
    builderId?: number;
    commentIDUpper?: number;
    jobName?: string;
    sourceType?: string;
    sourceTypeId?: number;
    linkedID?: number;
    commentDate?: moment.MomentInput;
    commentDateForMobile?: string;
    commentDateFormatted?: string | null;
    commentTimeFormatted?: string | null;
    commentDateUtc?: moment.MomentInput;
    userFullName?: string | null;
    userType?: string | BTLoginTypes | null;
    addedById?: number;
    byCurrentUser?: boolean;
    commentText?: string | null;
    commentJson?: IEditorApiType | null;
    commentsExists?: boolean;
    showOwner?: boolean;
    showSubs?: boolean;
    builderName?: string | null;
    documentLink?: string;
    contextMenu?: string;
    photo?: string | null;
    showCommentInfo?: boolean;
    parentId?: number | null;
    isUnread?: boolean;
    approvalStatus?: ApprovalStatus;
    reactions?: UserInteractionsContract[];
}

export class DiscussionCommentEntity implements IBaseEntity {
    constructor(data: IDiscussionCommentData) {
        this.id = data.commentID ?? 0;
        this.jobId = data.jobsiteID ?? 0;
        this.builderId = data.builderId ?? 0;

        this.commentIDUpper = data.commentIDUpper ?? 0;
        this.jobName = data.jobName ?? "";
        this.sourceType = data.sourceType ?? "";
        this.sourceTypeId = data.sourceTypeId ?? 0;
        this.linkedID = data.linkedID ?? 0;

        // date is sent down in local time
        this.commentDate = moment(data.commentDate);
        this.commentDateForMobile = data.commentDateForMobile ?? "";
        this.commentDateFormatted = data.commentDateFormatted ?? null;
        this.commentTimeFormatted = data.commentTimeFormatted ?? null;

        this.commentDateUtc = moment(data.commentDateUtc);

        this.userFullName = data.userFullName ?? null;
        this.userType = data.userType ?? null;
        this.addedById = data.addedById ?? 0;
        this.byCurrentUser = data.byCurrentUser ?? false;

        this.commentText = data.commentText ? data.commentText : null;
        this.commentJson = data.commentJson
            ? new EditorContent(data.commentJson)
            : EditorContent.CreateEmpty();
        this.commentIsJson = !!data.commentJson;
        this.commentExists = data.commentsExists ?? false;

        // place canAdd, canView, canDelete, canNotifyOwner, canNotifySubs, canShowOwner, canShowSubs, etc into a permissions object
        this.permissions = new CommentPermissions(data);

        this.showOwner = data.showOwner ?? false;
        this.showSubs = data.showSubs ?? false;

        this.canDelete = data.canDelete ?? false;

        this.builderName = data.builderName ?? null;
        this.documentLink = data.documentLink ?? "";
        this.contextMenu = data.contextMenu ?? "";
        this.photo = data.photo ?? null;
        this.showCommentInfo = data.showCommentInfo ?? false;
        this.parentId = data.parentId ?? null;
        this.isUnread = data.isUnread ?? false;
        this.approvalStatus = data.approvalStatus;

        this.reactions = [];
        if (data.reactions) {
            this.reactions = data.reactions.map((reaction: UserInteractionsContract) => ({
                ...reaction,
                reactDateUtc: moment(reaction.reactDateUtc),
                readDateUtc: moment(reaction.readDateUtc),
            })) as unknown as UserInteractionsContract[];
        }
    }

    toOwnerPreviewComment: () => DiscussionCommentEntity = () => {
        return {
            ...this,
            byCurrentUser: this.userType === "Owner",
            permissions: {
                ...this.permissions,
                canDelete: false,
            },
        };
    };

    id: number;
    jobId: number;
    builderId: number;

    commentIDUpper: number;
    jobName: string;

    /** Entity name, Example: "Warranty" */
    sourceType: string;
    sourceTypeId: number;
    linkedID: number;
    commentDate: moment.Moment;
    commentDateUtc: moment.Moment;
    commentDateForMobile: string;
    commentDateFormatted: string | null;
    commentTimeFormatted: string | null;
    userFullName: string | null;
    userType: string | BTLoginTypes | null;
    addedById: number;
    byCurrentUser: boolean;

    commentText: string | null;
    commentJson: EditorContent;
    commentIsJson: boolean;
    commentExists: boolean;

    permissions: CommentPermissions;

    showOwner: boolean;
    showSubs: boolean;

    canDelete: boolean;

    builderName: string | null;
    documentLink: string;
    contextMenu: string;
    photo: string | null;
    showCommentInfo: boolean;
    parentId: number | null;
    isUnread: boolean;

    approvalStatus?: ApprovalStatus;

    reactions: UserInteractionsContract[];
}

/** API response when adding a new comment */
export interface IAddCommentResponseData {
    comment: IDiscussionCommentData;
    channelId?: number | null;
}

export class AddCommentResponse {
    constructor(data: IAddCommentResponseData) {
        this.comment = new DiscussionCommentEntity(data.comment);
        this.channelId = data.channelId;
    }

    comment: DiscussionCommentEntity;
    channelId?: number | null;
}

/** API response when removing a new comment */
export class DeleteCommentResponse {
    // no data is returned from the api, only a status code
}

export interface INotifyPermissionRequest {
    linkedId: number;
    linkedType: DiscussionsLinkedTypes;
    notifyOwner: boolean;
    notifySubs: boolean;
}

/** API request when updating a new comments permissions */
export interface IUpdateCommentPermissionRequest {
    showOwner: boolean;
    showSubs: boolean;
}

/** API response when updating a new comments permissions */
export class UpdateCommentPermissionResponse {
    // no data is returned from the api, only a status code
}

/** API response when reading a comment */
export class ChannelReadResponse {
    // no data is returned from the api, only a status code
}
